import { useCallback, useEffect, useState } from 'react';
import type { FleetAssistantMessageView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

export const ASSISTANT_POLL_PENDING_MS = 3_000;
export const ASSISTANT_POLL_IDLE_MS = 15_000;

type Conversation = {
  /** `null` until the first read has answered. */
  readonly messages: ReadonlyArray<FleetAssistantMessageView> | null;
  readonly pending: boolean;
  readonly readFailed: boolean;
};

type ReadAt = { readonly delay: number; readonly n: number };

export type SendResult = { readonly ok: true } | { readonly ok: false; readonly code: string | undefined };

const errorCodeOf = (body: unknown): string | undefined =>
  (body as { error?: { code?: string } } | undefined)?.error?.code;

/**
 * The signed-in operator's conversation with the fleet assistant: read at once, then every 3 s while a turn is
 * waiting and every 15 s otherwise. A timeout chain, never an interval, so a slow answer never stacks reads. A failed
 * read keeps what is on screen.
 */
export function useAssistantConversation(questionId: string | null) {
  const [conversation, setConversation] = useState<Conversation>({ messages: null, pending: false, readFailed: false });
  const [readAt, setReadAt] = useState<ReadAt>({ delay: 0, n: 0 });

  useEffect(() => {
    let current = true;
    const next = (delay: number) => setReadAt({ delay, n: readAt.n + 1 });

    const read = async () => {
      try {
        const result = await apiClient.adminFleetAssistant.list({
          query: questionId === null ? {} : { questionId },
        });

        if (!current) return;

        if (result.ok) {
          setConversation({ messages: result.body.data, pending: result.body.pending, readFailed: false });
          next(result.body.pending ? ASSISTANT_POLL_PENDING_MS : ASSISTANT_POLL_IDLE_MS);

          return;
        }
      } catch {
        if (!current) return;
      }

      setConversation((previous) => ({ ...previous, readFailed: true }));
      next(ASSISTANT_POLL_IDLE_MS);
    };

    const timer = setTimeout(() => void read(), readAt.delay);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [readAt, questionId]);

  const send = useCallback(
    async (text: string): Promise<SendResult> => {
      const result = await apiClient.adminFleetAssistant.post({
        body: questionId === null ? { text } : { text, questionId },
      });

      if (!result.ok) return { ok: false, code: errorCodeOf(result.body) };

      const message = result.body;
      setConversation((previous) => ({
        messages: [...(previous.messages ?? []), message],
        pending: true,
        readFailed: false,
      }));
      setReadAt((previous) => ({ delay: 0, n: previous.n + 1 }));

      return { ok: true };
    },
    [questionId],
  );

  return { ...conversation, send };
}
